/**
 * React Hooks for Zustand Auth Store
 *
 * Provides React hooks to interact with the Zustand auth store,
 * enabling seamless integration between Zustand and React components.
 *
 * Features:
 * - Type-safe hooks
 * - Selective subscriptions for performance
 * - Backward compatibility with existing AuthProvider API
 * - Optimized re-renders
 */

import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { type AuthState, authStore } from "./auth-store";

/**
 * Main auth hook - returns full auth state and actions
 * Use this for components that need multiple auth properties
 */
export const useAuth = () => {
	return useStore(authStore);
};

/**
 * Selective auth state hook - only subscribe to specific properties
 * Use this for performance optimization when you only need specific values
 */
export const useAuthState = <T>(selector: (state: AuthState) => T) => {
	return useStore(authStore, selector);
};

/**
 * Auth status hook - only subscribes to authentication status
 * Optimized for components that only care about login state
 */
export const useAuthStatus = () => {
	return useStore(
		authStore,
		useShallow((state) => ({
			isAuthenticated: state.isAuthenticated,
			isLoading: state.isLoading,
			isInitializing: state.isInitializing,
			isPermissionsLoaded: state.isPermissionsLoaded,
		})),
	);
};

/**
 * Current user hook - only subscribes to user data
 * Optimized for components that only need user information
 */
export const useCurrentUser = () => {
	return useStore(authStore, (state) => state.user);
};

/**
 * Auth actions hook - returns only the action functions
 * Use this for components that only need to trigger auth actions
 */
export const useAuthActions = () => {
	return useStore(
		authStore,
		useShallow((state) => ({
			login: state.login,
			logout: state.logout,
			checkAuthenticationStatus: state.checkAuthenticationStatus,
			handleOAuthReturn: state.handleOAuthReturn,
			setError: state.setError,
			reset: state.reset,
		})),
	);
};

/**
 * Permission hooks - for role-based access control
 */
export const usePermissions = () => {
	return useStore(
		authStore,
		useShallow((state) => ({
			hasPermission: state.hasPermission,
			hasRole: state.hasRole,
			hasAnyPermission: state.hasAnyPermission,
			hasAllPermissions: state.hasAllPermissions,
		})),
	);
};

/**
 * Auth error hook - only subscribes to error state
 * Useful for error handling components
 */
export const useAuthError = () => {
	return useStore(authStore, (state) => state.error);
};

/**
 * Loading state hook - only subscribes to loading states
 * Useful for loading indicators
 */
export const useAuthLoading = () => {
	return useStore(
		authStore,
		useShallow((state) => ({
			isLoading: state.isLoading,
			isInitializing: state.isInitializing,
		})),
	);
};

/**
 * Backward compatibility hook - matches the old AuthProvider API
 * This allows existing components to work without changes
 */
export const useAuthProvider = () => {
	const state = useStore(authStore);

	// Return the same interface as the old AuthProvider
	return {
		// State
		isAuthenticated: state.isAuthenticated,
		isLoading: state.isLoading,
		isInitializing: state.isInitializing,
		isProcessingCallback: state.isProcessingCallback,
		user: state.user,
		error: state.error,
		lastAuthCheck: state.lastAuthCheck,

		// Actions (maintain same signatures)
		login: state.login,
		logout: state.logout,
		handleOAuthReturn: state.handleOAuthReturn,
		checkAuthenticationStatus: state.checkAuthenticationStatus,
		setCallbackProcessing: state.setCallbackProcessing,
		hasPermission: state.hasPermission,
		hasRole: state.hasRole,
		hasAnyPermission: state.hasAnyPermission,
		hasAllPermissions: state.hasAllPermissions,
		switchOrganization: state.switchOrganization,
		getUserOrganizations: state.getUserOrganizations,
		can: state.can,
		cannot: state.cannot,
	};
};

/**
 * Hook for components that need to trigger auth initialization
 * Useful for app-level components
 */
export const useAuthInitialization = () => {
	return useStore(authStore, (state) => state.initialize);
};

/**
 * Custom hook for conditional rendering based on auth state
 * Returns render helpers for common auth scenarios
 */
export const useAuthRender = () => {
	const { isAuthenticated, isLoading, isInitializing, isPermissionsLoaded } =
		useAuthStatus();

	return {
		// Render helpers
		showLoading:
			isLoading || isInitializing || (isAuthenticated && !isPermissionsLoaded),
		showAuthenticated:
			isAuthenticated && !isLoading && !isInitializing && isPermissionsLoaded,
		showUnauthenticated: !isAuthenticated && !isLoading && !isInitializing,
		showInitializing: isInitializing,

		// State flags
		isReady:
			!isLoading &&
			!isInitializing &&
			(!isAuthenticated || isPermissionsLoaded),
		isAuthenticating: isLoading && !isInitializing,
		isLoadingPermissions: isAuthenticated && !isPermissionsLoaded,
	};
};

/**
 * Hook for auth-dependent navigation
 * Provides utilities for redirect logic
 */
export const useAuthNavigation = () => {
	const { isAuthenticated, isLoading, isInitializing, isPermissionsLoaded } =
		useAuthStatus();

	const shouldRedirectToLogin =
		!isAuthenticated && !isLoading && !isInitializing;
	const shouldAllowAccess =
		isAuthenticated && !isLoading && !isInitializing && isPermissionsLoaded;
	const shouldShowLoading =
		isLoading || isInitializing || (isAuthenticated && !isPermissionsLoaded);

	return {
		shouldRedirectToLogin,
		shouldAllowAccess,
		shouldShowLoading,
		isReady:
			!isLoading &&
			!isInitializing &&
			(!isAuthenticated || isPermissionsLoaded),
		isLoadingPermissions: isAuthenticated && !isPermissionsLoaded,
	};
};

/**
 * Development/debugging hook
 * Provides additional utilities for development
 */
export const useAuthDebug = () => {
	const state = useStore(authStore);

	return {
		// Full state for debugging
		fullState: state,

		// Quick state summary
		stateSummary: {
			isAuthenticated: state.isAuthenticated,
			isLoading: state.isLoading,
			isInitializing: state.isInitializing,
			hasUser: !!state.user,
			hasError: !!state.error,
			lastCheck: state.lastAuthCheck
				? new Date(state.lastAuthCheck).toLocaleTimeString()
				: "Never",
		},

		// Actions for testing
		testActions: {
			reset: state.reset,
			setError: state.setError,
			setLoading: state.setLoading,
		},
	};
};
